/**
 * Login — Phase-2 multi-currency rewrite.
 *
 * Single "Username or email" input. Sends `{login, password}` (backend
 * accepts either identifier against `username_lower` or `email`). The
 * legacy `{email, password}` shape kept working through Phase 1 for
 * compat with older builds — Phase-2 clients use the new shape.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { extractError } from "../lib/formatError";
import { AuthShell, TextField } from "./Signup";
import Button from "../components/ui-r1/Button";

export default function Login() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  const nextPath = searchParams.get("next");
  const expired = searchParams.get("expired");
  const [form, setForm] = useState({ login: "", password: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav(user.is_admin ? "/admin" : (nextPath || "/dashboard"), { replace: true });
  }, [user, nav, nextPath]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      const me = await login(data.access_token);
      const dest = nextPath || (me?.is_admin ? "/admin" : "/dashboard");
      nav(dest, { replace: true });
    } catch (e2) {
      const status = e2?.response?.status;
      if (status === 451) {
        nav("/blocked", { replace: true });
        return;
      }
      setError(extractError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your account" testid="login-page">
      {expired && (
        <div className="text-xs text-gold border border-gold/60 bg-gold/5 rounded-lg px-3 py-2 mb-4" data-testid="session-expired-banner">
          Your session expired. Please log in again.
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <TextField
          label="Username or email" type="text"
          value={form.login} onChange={change("login")}
          testid="login-identifier" required autoComplete="username"
          placeholder="sasha88 or you@example.com"
        />
        <TextField
          label="Password" type="password"
          value={form.password} onChange={change("password")}
          testid="login-password" required autoComplete="current-password"
        />
        {error && (
          <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2" data-testid="login-error">
            {error}
          </div>
        )}
        <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full" data-testid="login-submit-btn">
          {busy ? "Signing in…" : "Log in"}
        </Button>
        <div className="flex justify-between text-xs text-ink-2">
          <Link to="/forgot-password" className="hover:text-ink" data-testid="forgot-password-link">
            Forgot password?
          </Link>
          <Link to="/signup" className="text-pink hover:underline" data-testid="login-to-signup-link">
            Create account
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
